/**
 * O resumo da conferência (E153.F3, ADR-0104 §2.6): a situação do resultado como o servidor a diz,
 * a etapa `resultado` conferida agora com os critérios dela, a identidade do snapshot e a
 * conferência de veracidade da avaliação. Nada aqui é decidido na tela.
 */
import { Badge, Blueprint } from '@/components/ui';
import type { EtapaDoCasoData } from '@/lib/conferencia_das_etapas';
import type { VeracidadeDaAvaliacaoData } from '@/lib/formalizacaoAvaliacao';
import {
  codigoCurto,
  type ResultadoAuditadoData,
  type ResultadoDoCasoData,
} from '@/lib/resultadoAuditado';

import { textoDaCondicao, textoDoFundamento } from '../ConferenciaDasEtapas/textos';
import {
  VARIANTE_DA_LIBERACAO,
  VARIANTE_DA_SITUACAO,
  VARIANTE_DA_VERACIDADE,
  dataCurta,
  preencher,
  t,
  te,
  tf,
} from './textos';

function Etapa({ etapa }: { etapa: EtapaDoCasoData }): JSX.Element {
  const { agora } = etapa;
  if (agora === null) {
    const aguardando = etapa.aguardando;
    const texto =
      aguardando?.motivo === 'etapa_anterior'
        ? preencher(te.aguardando.etapa_anterior, {
            etapa: te.etapa[aguardando.depende_de ?? 'analise'],
          })
        : te.aguardando.nao_executada;
    return (
      <p className="text-body-sm text-fg" data-testid="resultado-etapa" data-liberacao="aguardando">
        {t.resumo.etapa}: <Badge>{te.liberacao.aguardando}</Badge> {texto}
      </p>
    );
  }
  return (
    <div
      className="space-y-sc-1 text-body-sm"
      data-testid="resultado-etapa"
      data-liberacao={agora.liberacao}
    >
      <p className="text-fg">
        {t.resumo.etapa}:{' '}
        <Badge variant={VARIANTE_DA_LIBERACAO[agora.liberacao]}>
          {te.liberacao[agora.liberacao]}
        </Badge>{' '}
        {textoDaCondicao(agora.condicao_de_saida)}
      </p>
      <ul className="space-y-sc-1 text-fg-muted">
        {agora.criterios.map((c) => (
          <li key={c.chave} data-testid="resultado-criterio-da-etapa" data-resultado={c.resultado}>
            {te.criterio[c.chave]}: {textoDoFundamento(c)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
  return (
    <p>
      <span className="text-fg-muted">{rotulo}</span> <span className="text-fg">{valor}</span>
    </p>
  );
}

function Identidade({ r }: { r: ResultadoAuditadoData }): JSX.Element {
  const i = r.identidade;
  return (
    <div
      className="grid grid-cols-1 gap-x-sc-4 gap-y-sc-1 text-body-sm md:grid-cols-2"
      data-testid="resultado-identidade"
      data-rodada={i.rodada}
    >
      <p className="text-fg">
        {preencher(t.resumo.caso, { codigo: i.display_code })} ·{' '}
        {preencher(t.resumo.rodada, { rodada: i.rodada })} · {tf.modo[i.modo]}
      </p>
      <p className="text-fg-muted">
        {preencher(t.resumo.registradaEm, { data: dataCurta(i.criada_em) })} ·{' '}
        {preencher(t.resumo.pecas, { n: i.pecas.length })}
      </p>
      <Linha rotulo={t.resumo.codigo} valor={codigoCurto(i.resultado_sha256)} />
      <Linha rotulo={t.resumo.entrada} valor={codigoCurto(i.entrada_sha256)} />
      <Linha
        rotulo={t.resumo.fluxo}
        valor={i.fluxo_sha256 === null ? t.resumo.semFluxo : codigoCurto(i.fluxo_sha256)}
      />
    </div>
  );
}

function Veracidade({ qa }: { qa: VeracidadeDaAvaliacaoData }): JSX.Element {
  return (
    <div className="space-y-sc-1 text-body-sm" data-testid="resultado-qa" data-estado={qa.estado}>
      <p className="text-fg">
        {t.resumo.qaTitulo}:{' '}
        <Badge variant={VARIANTE_DA_VERACIDADE[qa.estado]}>{t.qa[qa.estado]}</Badge>{' '}
        <span className="text-fg-muted">{preencher(t.resumo.qaVersao, { versao: qa.versao })}</span>
      </p>
      <ul className="space-y-sc-1 text-fg-muted">
        {qa.criterios.map((c) => (
          <li key={c.chave} data-testid="resultado-criterio-qa" data-estado={c.estado}>
            {tf.criterio[c.chave]}: {tf.estadoDoCriterio[c.estado]}
            {c.bloqueante ? ` · ${t.resumo.bloqueante}` : ''} · {c.detalhe}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ResumoDoResultado({ dados }: { dados: ResultadoDoCasoData }): JSX.Element {
  return (
    <Blueprint
      as="section"
      className="space-y-sc-3 p-sc-6"
      data-testid="resultado-resumo"
      data-situacao={dados.situacao}
      aria-labelledby="resultado-resumo-titulo"
    >
      <h2 id="resultado-resumo-titulo" className="text-body-lg text-fg">
        {t.resumo.titulo}
      </h2>
      <p className="text-body-md text-fg">
        <Badge variant={VARIANTE_DA_SITUACAO[dados.situacao]}>{t.situacao[dados.situacao]}</Badge>{' '}
        <span data-testid="resultado-consumivel" data-consumivel={dados.consumivel}>
          {dados.consumivel ? t.resumo.consumivel : t.resumo.naoConsumivel}
        </span>
      </p>
      <p className="max-w-prose text-body-sm text-fg-muted">{t.situacaoTexto[dados.situacao]}</p>
      {dados.etapa !== null ? <Etapa etapa={dados.etapa} /> : null}
      {dados.resultado !== null ? (
        <>
          <Identidade r={dados.resultado} />
          <Veracidade qa={dados.resultado.qa} />
        </>
      ) : null}
    </Blueprint>
  );
}
